/**
 * MASTER SHEET COLUMNS
 */

import { ColumnDefinition, COLUMN_PRESETS } from '../../../universal_table';

export const MASTER_SHEET_COLUMNS: ColumnDefinition[] = [
  {
    key: 'entry_date',
    label: 'Date',
    unitLabel: '',
    ...COLUMN_PRESETS.DATE,
    sticky: true,
    minWidth: 110
  },
  {
    key: 'plant_capacity_mw',
    label: 'AC Capacity (MW)',
    unitLabel: 'MW',
    abbreviation: 'AC Capacity',
    ...COLUMN_PRESETS.READONLY,
    minWidth: 120
  },
  {
    key: 'plant_capacity_mwp',
    label: 'DC Capacity (MWp)',
    unitLabel: 'MWp',
    abbreviation: 'DC Capacity',
    ...COLUMN_PRESETS.READONLY,
    minWidth: 120
  },
  {
    key: 'budgeted_energy_mwh',
    label: 'Budgeted Energy',
    unitLabel: 'MWh',
    abbreviation: 'Budgeted Energy',
    ...COLUMN_PRESETS.NUMBER,
    minWidth: 100
  },
  {
    key: 'gii_kwh_per_m2',
    label: 'Budgeted GII',
    unitLabel: 'kWh/m²',
    abbreviation: 'Budgeted GII',
    ...COLUMN_PRESETS.NUMBER,
    minWidth: 100
  },
  {
    key: 'tariff_inr_per_kwh',
    label: 'Tariff',
    unitLabel: 'INR/ kWh',
    abbreviation: 'Tariff',
    ...COLUMN_PRESETS.CALCULATED,
    calculateFn: () => '2.8',
    minWidth: 100
  },
  {
    key: 'ghi_kwh_per_m2',
    label: 'GHI',
    unitLabel: 'kWh/m²',
    abbreviation: 'GHI',
    ...COLUMN_PRESETS.NUMBER,
    minWidth: 100
  },
  {
    key: 'avg_ambient_temp_c',
    label: 'Ambient Temp (Avg)',
    unitLabel: '°C',
    abbreviation: 'Ambient Temp (Avg)',
    ...COLUMN_PRESETS.NUMBER,
    minWidth: 120
  },
  {
    key: 'avg_module_temp_c',
    label: 'Module Temp (Avg)',
    unitLabel: '°C',
    abbreviation: 'Module Temp (Avg)',
    ...COLUMN_PRESETS.NUMBER,
    minWidth: 120
  },
  {
    key: 'avg_air_speed_ms',
    label: 'WS',
    unitLabel: 'm/s',
    abbreviation: 'WS',
    ...COLUMN_PRESETS.NUMBER,
    minWidth: 100
  },
  {
    key: 'export_mwh',
    label: 'Energy Export',
    unitLabel: 'MWh',
    abbreviation: 'Energy Export',
    ...COLUMN_PRESETS.NUMBER,
    minWidth: 100
  },
  {
    key: 'import_mwh',
    label: 'Energy Import',
    unitLabel: 'MWh',
    abbreviation: 'Energy Import',
    ...COLUMN_PRESETS.NUMBER,
    minWidth: 100
  },
  {
    key: 'net_energy_mwh',
    label: 'Net Energy',
    unitLabel: 'MWh',
    abbreviation: 'Net Energy',
    ...COLUMN_PRESETS.CALCULATED,
    calculateFn: (rowData) => {
      // Net Energy = Export Energy - Import Energy
      const exportVal = parseFloat(rowData.export_mwh) || 0;
      const importVal = parseFloat(rowData.import_mwh) || 0;
      return (exportVal - importVal).toFixed(3);
    },
    dependencies: ['export_mwh', 'import_mwh'],
    minWidth: 100
  },
  {
    key: 'energy_mtd_mwh',
    label: 'Energy MTD',
    unitLabel: 'MWh',
    abbreviation: 'Energy MTD',
    ...COLUMN_PRESETS.CALCULATED,
    minWidth: 100
  },
  {
    key: 'energy_ytd_mwh',
    label: 'Energy YTD',
    unitLabel: 'MWh',
    abbreviation: 'Energy YTD',
    ...COLUMN_PRESETS.CALCULATED,
    minWidth: 100
  },
  {
    key: 'inverter_energy_mwh',
    label: 'Inverter Energy',
    unitLabel: 'MWh',
    abbreviation: 'Inverter Energy',
    ...COLUMN_PRESETS.CALCULATED,
    minWidth: 110
  },
  {
    key: 'auxiliary_kwh',
    label: 'Auxiliary Consumption',
    unitLabel: 'MWh',
    abbreviation: 'Auxiliary Consumption',
    ...COLUMN_PRESETS.NUMBER,
    minWidth: 130
  },
  {
    key: 'pa_time_percent',
    label: 'PA (Time)',
    unitLabel: '%',
    abbreviation: 'PA (Time)',
    ...COLUMN_PRESETS.CALCULATED,
    minWidth: 90
  },
  {
    key: 'plant_downtime_energy_loss_mwh',
    label: 'Plant Downtime Energy Loss',
    unitLabel: 'MWh',
    abbreviation: 'Plant Downtime Energy Loss',
    ...COLUMN_PRESETS.CALCULATED,
    minWidth: 180
  },
  {
    key: 'plant_availability_percent',
    label: 'PA (Energy)',
    unitLabel: '%',
    abbreviation: 'PA (Energy)',
    ...COLUMN_PRESETS.NUMBER,
    minWidth: 90
  },
  {
    key: 'ga_time_percent',
    label: 'GA (Time)',
    unitLabel: '%',
    abbreviation: 'GA (Time)',
    ...COLUMN_PRESETS.CALCULATED,
    minWidth: 90
  },
  {
    key: 'grid_downtime_energy_loss_mwh',
    label: 'Grid Downtime Energy Loss',
    unitLabel: 'MWh',
    abbreviation: 'Grid Downtime Energy Loss',
    ...COLUMN_PRESETS.CALCULATED,
    minWidth: 180
  },
  {
    key: 'grid_availability_percent',
    label: 'GA (Energy)',
    unitLabel: '%',
    abbreviation: 'GA (Energy)',
    ...COLUMN_PRESETS.NUMBER,
    minWidth: 90
  },
  {
    key: 'plf_percent',
    label: 'PLF',
    unitLabel: '%',
    abbreviation: 'PLF',
    ...COLUMN_PRESETS.READONLY,
    minWidth: 75
  },
  {
    key: 'budgeted_pr_percent',
    label: 'Budgeted PR',
    unitLabel: '%',
    abbreviation: 'Budgeted PR',
    ...COLUMN_PRESETS.NUMBER,
    minWidth: 100
  },
  {
    key: 'performance_ratio_percent',
    label: 'PR',
    unitLabel: '%',
    abbreviation: 'PR',
    ...COLUMN_PRESETS.READONLY,
    minWidth: 75
  },
  {
    key: 'specific_yield_kwh_per_kwp',
    label: 'Specific Yield',
    unitLabel: 'MWh/MWp',
    abbreviation: 'Specific Yield',
    ...COLUMN_PRESETS.READONLY,
    minWidth: 100
  },
  {
    key: 'weather',
    label: 'Weather',
    unitLabel: '',
    abbreviation: 'Weather',
    type: 'text',
    minWidth: 100,
    format: (value: any) => {
      if (!value) return '';
      const weatherValue = String(value).trim();

      // Weather emoji mapping - only weather values that exist in database
      const weatherEmojiMap: Record<string, string> = {
        'Sunny': '☀️',
        'Mostly Sunny': '🌤️',
        'Partly Cloudy': '⛅',
        'Cloudy': '☁️',
        'Cloudy & Dusty': '☁️🌪️',
        'Partly Cloudy & Dusty': '⛅🌪️',
        'Partly Cloudy & Rainfall': '⛅🌦️'
      };

      const emoji = weatherEmojiMap[weatherValue] || '';
      return emoji ? `${weatherValue} ${emoji}` : weatherValue;
    },
    parse: (input: string) => {
      // Remove emoji and extra spaces when parsing user input
      if (!input) return '';
      // Remove emoji characters (comprehensive Unicode ranges for emojis)
      // This covers most emoji ranges including weather-related emojis
      return input
        .replace(/[\u{1F300}-\u{1F9FF}]/gu, '') // Miscellaneous Symbols and Pictographs
        .replace(/[\u{2600}-\u{26FF}]/gu, '') // Miscellaneous Symbols (includes weather symbols)
        .replace(/[\u{2700}-\u{27BF}]/gu, '') // Dingbats
        .trim();
    }
  },
  {
    key: 'remarks',
    label: 'Remarks',
    unitLabel: '',
    type: 'text',
    width: 'auto',
    minWidth: 150
  }
];
